<template>
	<div
		ref="splitbutton"
		:class="[
			'split-button',
			variantName ? 'split-button--' + variantName : ''
		]"
		:id="`${uid}`"
	>
		<div class="split-button__input-container" ref="dropdownPosRef">
			<Spinner
				class="btn-spinner-overlay"
				v-if="isLoading"
				:processor="{ loading: true }"
				:variant="'buttons'"
				data-test="spinner"
			/>
			<Button
				:class="[
					'btn',
					variantName ? 'btn--' + variantName : '',
					'split-button__primary-action'
				]"
				:btnClick="clickButton"
				:disabled="disabled"
			>
				<span class="split-button__unsaved" v-show="pendingChanges">
					<i class="icon icon-unsaved"></i>
				</span>
				<span class="split-button__text">{{ value.text }}</span>
			</Button>
			<span class="split-button__separator"></span>
			<Button
				:class="[
					'btn',
					variantName ? 'btn--' + variantName : '',
					'split-button__more-actions'
				]"
				:btnClick="toggleList"
				:disabled="disabled"
				data-test="btnToggleList"
			>
				<div class="split-button__icon">
					<i class="icon icon-caret_down"></i>
				</div>
			</Button>
		</div>
		<DropdownList
			ref="dropdownListRef"
			:id="`${uid}_ddl`"
			:isOpen="isOpen"
			:options="items"
			@change="changeSelectedItem"
			data-test="list"
		/>
		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="isOpen"
			strategy="fixed"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
			ref="popperPosition"
		/>
	</div>
</template>
<script>
import DropdownList from '@/components/forms/dropdown/dropdown-list';
import DocumentClickedMixin from '@/mixins/document-click';
import Button from '@/components/forms/button/button';
import Spinner from '@/components/visual/spinner/spinner';
import PopperPosition from '@/components/visual/popper-position';

// mixins
import popperPositionMixin from '@/mixins/popper-position-mixin';

export default {
	name: 'SplitButton',
	mixins: [DocumentClickedMixin('closeHandler'), popperPositionMixin],
	inheritAttrs: false,
	components: {
		DropdownList,
		Button,
		Spinner,
		PopperPosition
	},
	model: {
		event: 'click'
	},
	props: {
		variantName: {
			type: String,
			default: ''
		},
		items: {
			type: Array,
			required: true
		},
		pendingChanges: {
			type: Boolean,
			default: true
		},
		value: {
			type: Object,
			required: true
		},
		isLoading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			dispatchDropUp: null,
			isOpen: false
		};
	},
	mounted() {
		document.addEventListener('scroll', this.changeScrollPosition);
		this.changeScrollPosition();
	},
	beforeDestroy() {
		document.removeEventListener('scroll', this.changeScrollPosition);
	},
	methods: {
		changeSelectedItem(value) {
			this.isOpen = false;
			const item = this.items.find(op => op.value === value);
			this.$emit('click', item);
		},
		changeScrollPosition() {
			this.dispatchDropUp =
				window.innerHeight -
					this.$refs.splitbutton.getBoundingClientRect().top <
				100;
		},
		clickButton() {
			if (this.isLoading) {
				this.isOpen = false;
				return;
			}
			if (this.isOpen) {
				this.isOpen = false;
			}
			this.$emit('click', this.value);
		},
		closeHandler() {
			this.isOpen = false;
		},
		toggleList() {
			if (!this.isLoading) {
				this.isOpen = !this.isOpen;
			} else {
				this.isOpen = false;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Split Button Styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.split-button {
	display: inline-flex;
	position: relative;

	&__input-container {
		position: relative;
		display: flex;
		background-color: $btn-default-bkgd;
		border-radius: $btn-border-radius;
		border: $btn-border-width solid $base;
	}

	&__primary-action,
	&__more-actions {
		appearance: none;
		border: none;
		padding-top: $btn-vertical-padding - $btn-border-width;
		padding-bottom: $btn-vertical-padding - $btn-border-width;
		@include btn-height($container-border: $btn-border-width);
	}

	&__primary-action {
		border-radius: ($btn-border-radius - 1) 0 0 ($btn-border-radius - 1);
		padding-left: $btn-horizontal-padding + 4;
		padding-right: $btn-horizontal-padding + 4;
	}

	&__unsaved {
		margin-right: $gutter-md;
	}

	&__separator {
		width: 1px;
		background-color: $neutral-1;
		margin: 4px 0;
	}

	&__more-actions {
		font-size: $input-font-size + 2;
		z-index: 0;
		min-width: 33px;
		border-radius: 0 ($btn-border-radius - 1) ($btn-border-radius - 1) 0;
	}

	&__icon {
		display: flex;
		padding: $padding-xs;
		font-size: 8px;
		cursor: pointer;
	}

	&.is-upward {
		.split-button__icon {
			.icon {
				transform: rotate(180deg);
			}
		}
	}
}

.split-button--primary {
	.split-button__input-container {
		background-color: $btn-primary-bkgd;
	}

	.split-button__separator {
		background-color: $white;
	}
}

.split-button--secondary {
	.split-button__input-container {
		background-color: $btn-secondary-bkgd;
		border-color: $btn-secondary-bkgd;
	}
}

/*------------------------------------*
#SPLIT BUTTON SPINNER
\*------------------------------------*/
.split-button__input-container {
	.btn-spinner-overlay {
		position: absolute;
		top: -$btn-border-width;
		bottom: -$btn-border-width;
		left: -$btn-border-width;
		right: -$btn-border-width;
		margin: auto;
		background-color: rgba($black, 0.6);
		border-radius: $btn-border-radius;
		backdrop-filter: blur(2px);
		z-index: 5;
		cursor: wait;
	}
}
</style>
